import * as React from 'react'
import type { components } from './services/schema'

type SignInResponse = components['schemas']['SignInResponseDto']

export interface AuthContext {
  isAuthenticated: boolean
  login: (data: SignInResponse) => void
  logout: () => void
  user: SignInResponse | null
}

const AuthContext = React.createContext<AuthContext | null>(null)

const key = 'auth.user'

function getStoredUser(): SignInResponse | null {
  const stored = localStorage.getItem(key)
  return stored ? JSON.parse(stored) : null
}

function setStoredUser(user: SignInResponse | null) {
  if (user) {
    localStorage.setItem(key, JSON.stringify(user))
  } else {
    localStorage.removeItem(key)
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AuthContext['user']>(getStoredUser())
  const isAuthenticated = !!user

  const logout = React.useCallback(() => {
    setStoredUser(null)
    setUser(null)
  }, [])

  const login = React.useCallback((data: SignInResponse) => {
    setStoredUser(data)
    setUser(data)
  }, [])

  React.useEffect(() => {
    setUser(getStoredUser())
  }, [])

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = React.useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
